// Wire contract for the wiser-accounts service. MUST stay in lockstep with the Rust DTOs
// (src/handlers.rs). TRANSPORT only — the server is content-blind, so all fields are base64 blobs the
// caller computes/consumes with @wiser/e2e (deriveKEK / generateMasterKey / wrapMasterKey / keypair).

/** The blobs returned after auth so the client can unwrap its keys LOCALLY. Never includes the verifier. */
export interface AccountBlobs {
  user_id: string;
  email: string;
  display_name: string | null;
  /** Org/workspace scope (null in personal mode) — used to scope directory lookups. */
  tenant_id: string | null;
  /** X25519 public key (also in wiserkeys). */
  public_key_b64: string;
  /** Master key wrapped under the KEK (Argon2id(password, kdf_salt)) — unwrap client-side. */
  wrapped_master_key_b64: string;
  /** Private key wrapped under the master key — unwrap client-side. */
  wrapped_private_key_b64: string;
  /** Salt the client uses to re-derive the auth key + KEK. */
  kdf_salt_b64: string;
  /** Whether the account's email has been verified. Login is refused until this is true. */
  email_verified: boolean;
  created_at: string;
  /** Resolved org session-security policy (0018) for this account's tenant. Present only when the account
   *  belongs to an org with a policy; absent otherwise (the client applies its own defaults). The suite reads
   *  it and applies it as a FLOOR over the local user preference (org sets the minimum, never the maximum). */
  org_policy?: OrgPolicy;
}

/** An org's session-security posture (0018). Mirrors wiser-suite's SecurityConfig 1:1. */
export interface OrgPolicy {
  /** Require a system-authentication (polkit: password / fingerprint / security key) to re-unlock. */
  require_reauth_unlock: boolean;
  /** Idle minutes before an automatic lock. 0 = never auto-lock on idle. */
  auto_lock_minutes: number;
  lock_on_screen_lock: boolean;
  lock_on_sleep: boolean;
}

export interface SignupInput {
  email: string;
  display_name?: string;
  tenant_id?: string;
  /** SHA-256(auth_key) — the login verifier. The server never sees the password or auth_key. */
  auth_verifier_b64: string;
  kdf_salt_b64: string;
  wrapped_master_key_b64: string;
  public_key_b64: string;
  wrapped_private_key_b64: string;
  recovery_verifier_b64?: string;
  recovery_wrapped_b64?: string;
  /** Invite code — signup is invite-gated server-side. A code flagged `bypass_verification` creates the
   *  account already email-verified (internal/testing). */
  invite_code?: string;
}

/** Signup no longer mints a session — the account is created unverified and the user must verify by email
 *  (via the emailed link) before they can log in. */
export interface SignupResult { user_id: string; verification_required: true; }
export interface LoginResult {
  session_token: string;
  account: AccountBlobs;
  /** Present ONLY when the login opted into "keep me signed in" (`issueRefresh`, 0017): a long-lived,
   *  revocable token to store in the OS keychain and later trade via `refreshSession`. Absent otherwise. */
  refresh_token?: string;
}
/** Result of `refreshSession` — a fresh session plus a ROTATED refresh token (the presented one is now dead).
 *  Persist the new `refresh_token`; the session grants IDENTITY only (crypto apps still unlock with the pw). */
export interface RefreshResult { session_token: string; account: AccountBlobs; refresh_token: string; }
/** Login was correct but the account gates unrecognised devices (0015): an emailed PIN must be confirmed via
 *  `deviceChallenge` before a session is issued. Returned INSTEAD of a session from `login`. */
export interface NewDeviceChallenge { challenge: 'new_device'; email: string; }
export function isNewDeviceChallenge(r: LoginResult | NewDeviceChallenge): r is NewDeviceChallenge {
  return (r as NewDeviceChallenge).challenge === 'new_device';
}
/** Body for `deviceChallenge` — re-prove the password + confirm the emailed PIN for THIS device. */
export interface DeviceChallengeInput {
  email: string;
  auth_verifier_b64: string;
  device_id: string;
  pin: string;
  /** Opt into "keep me signed in" (0017) while completing the challenge on a new device → the LoginResult
   *  carries a `refresh_token`. */
  issue_refresh?: boolean;
}
/** Result of confirming an email-verification token (and of resend, which returns { ok }). */
export interface VerifyEmailResult { ok: boolean; email: string; }
/** The per-account KDF salt for deriving the login verifier. NOT an account-existence oracle: an UNKNOWN
 *  email returns a 200 with a STABLE decoy salt + synthesized user_id (indistinguishable from a real hit),
 *  and login then fails with the same generic 401 as a wrong password. Never expect a 404 from lookup. */
export interface LookupResult { user_id: string; kdf_salt_b64: string; }
export interface RecoverResult { recovery_wrapped_b64: string; }

/** Content-blind password change. The client re-derives EVERYTHING under the new password (new salt, new
 *  KEK, new verifier, master key re-wrapped under the new KEK) and proves the CURRENT verifier here. The
 *  master key + keypair are unchanged, so existing encrypted data stays openable. Kills existing sessions. */
export interface ChangePasswordInput {
  email: string;
  /** SHA-256(current auth_key) — proves the caller knows the current password. */
  current_auth_verifier_b64: string;
  /** SHA-256(new auth_key) under the new password. */
  new_auth_verifier_b64: string;
  /** New per-user KDF salt. */
  new_kdf_salt_b64: string;
  /** Master key re-wrapped under the new KEK. */
  new_wrapped_master_key_b64: string;
}

/** Configure (or replace) the account's recovery key. Re-proves the current password (sensitive change).
 *  Content-blind: the server stores only the recovery verifier + the opaque master-key-wrapped-under-recovery. */
export interface SetupRecoveryInput {
  email: string;
  /** SHA-256(current auth_key) — re-prove the password. */
  current_auth_verifier_b64: string;
  /** SHA-256(recovery_key) — proof later presented to /recover + /recover-reset. */
  recovery_verifier_b64: string;
  /** Master key wrapped under the recovery key. */
  recovery_wrapped_b64: string;
  /** Random per-account salt the recovery key was derived under. Stored so the recovery key is INDEPENDENT of
   *  the email — the recovery code then survives a change-email (and password reset). 16 bytes. Accounts set
   *  up before this field existed have a NULL salt server-side and fall back to the legacy email-derived salt. */
  recovery_salt_b64: string;
}

/** Forgot-password reset authenticated by the RECOVERY key (not the old password) PLUS the one-time PIN
 *  emailed by resetRequest(). The client has already recovered the master key (via recover()) and re-wrapped
 *  it under a new password. Both factors are required. The recovery key itself is unchanged, so it stays
 *  valid for next time. Kills existing sessions. */
export interface RecoverResetInput {
  email: string;
  /** Same recovery verifier as recover(). */
  recovery_verifier_b64: string;
  /** The one-time 10-digit PIN from resetRequest()'s email (mailbox-control proof). */
  reset_pin: string;
  new_auth_verifier_b64: string;
  new_kdf_salt_b64: string;
  new_wrapped_master_key_b64: string;
}

/** Whether the account has a recovery key configured. `false` (not an error) for an unknown email — reveals
 *  no more than lookup() already does. Lets the settings UI show an accurate "recovery: active/not set".
 *
 *  `recovery_salt_b64` is the (non-secret) random salt the recovery key was derived under, so the reset flow
 *  can re-derive the recovery key from the entered code WITHOUT the email. It is `null` when recovery is not
 *  configured OR for a LEGACY account whose recovery key was derived under the old email-based salt — in the
 *  legacy case the client falls back to deriving the salt from the email. */
export interface RecoveryStatusResult { configured: boolean; recovery_salt_b64: string | null; }

/** One active session for the signed-in account (see AccountsApi.listSessions). Carries only display metadata
 *  and a stable `session_id` for targeted revocation — NEVER the bearer token of this or any other session. */
export interface SessionInfo {
  /** Stable opaque id (NOT the bearer token) — pass to revokeSessions to revoke this specific session. */
  session_id: string;
  created_at: string;
  last_seen_at: string;
  /** Best-effort User-Agent captured when the session was created. */
  user_agent: string | null;
  /** Truncated client IP (e.g. /24 or /48) recorded at session creation — locate-ish without full-IP logging. */
  ip_prefix: string | null;
  /** True for the session making this request (the "this device" row). */
  current: boolean;
}

/** Revoke sessions for the signed-in account. Provide `session_id` to revoke one specific session, or
 *  `all_others: true` to revoke every session EXCEPT the presenting one ("sign out everywhere else"). */
export interface RevokeSessionsInput {
  session_id?: string;
  all_others?: boolean;
}

/** Change the signed-in account's login email. Re-proves the current password; the new address is confirmed
 *  by a one-time PIN emailed to it (see AccountsApi.changeEmailRequest). Content-blind: the login crypto is
 *  email-independent, so nothing is re-wrapped — EXCEPT a legacy account whose recovery key used the old
 *  email-derived salt, which must re-key recovery under a fresh random salt so the recovery code survives. */
export interface ChangeEmailInput {
  new_email: string;
  /** One-time PIN emailed to `new_email` by changeEmailRequest. */
  change_pin: string;
  /** SHA-256(current auth_key) — re-prove the password (an unlocked session alone must not move the account). */
  current_auth_verifier_b64: string;
  /** LEGACY-recovery accounts only: re-key recovery under a fresh random salt (derived from the recovery code)
   *  so the code still works after the email changes. Omit for new-style (decoupled) recovery or no recovery. */
  recovery_verifier_b64?: string;
  recovery_wrapped_b64?: string;
  recovery_salt_b64?: string;
}

/** Self-service account deletion (soft delete + grace-period purge). Re-proves the password and requires a
 *  typed email confirmation. The account is flagged deleted and all sessions revoked immediately; login is
 *  refused while flagged; a background purge hard-deletes it after the grace window. */
export interface DeleteAccountInput {
  /** SHA-256(current auth_key) — re-prove the password (deletion is destructive). */
  current_auth_verifier_b64: string;
  /** Must equal the account's own email, or the delete is refused (typed-confirmation guard). */
  confirm_email: string;
}

/** One identity record on the /accounts/changes feed (Theme A). Carries only identity fields — never the
 *  verifier or wrapped blobs. */
export interface IdentityChange {
  /** BIGSERIAL cursor; pullers store the max seen and re-poll with since=that. */
  change_seq: number;
  user_id: string;
  tenant_id: string | null;
  email: string;
  display_name: string | null;
}

/** A page of the resumable identity feed. Store `next_since` and re-poll with `since=next_since`. */
export interface ChangesResult { changes: IdentityChange[]; next_since: number; }

/** One entry from the shared audit trail (0021/0022/0023) — the contract BOTH the list (`/org/audit`,
 *  `/admin/audit`) and per-entry detail (`/org/audit/{id}`, `/admin/audit/{id}`) endpoints return. Carries
 *  the device context (ip + user_agent) and, for logins, the originating dashboard/app (`client`, derived
 *  server-side from `detail.client`). Shared with the `@wiser/audit` <AuditLog>/<AuditDetail> components. */
export interface AuditEntry {
  /** uuid primary key of the audit row (stable id for the detail route). */
  id: string;
  /** created_at as an ISO-8601 string. */
  when: string;
  /** e.g. "auth.login", "org.member.role", "account.disable". */
  action: string;
  actor_email: string;
  actor_user_id?: string | null;
  target_email?: string | null;
  target_user_id?: string | null;
  /** Source IP (coarse /24-or-/48 prefix for logins + org actions; raw for console actions). */
  ip?: string | null;
  /** Device user-agent (best-effort, 256-char cap). */
  user_agent?: string | null;
  /** The dashboard/app that performed a LOGIN (audit provenance); null for every non-login action. */
  client?: string | null;
  /** The action-specific detail blob (opaque JSON; shape varies by action). */
  detail: Record<string, unknown>;
}

/** Optional filters for the audit list endpoints. All optional; absent = no constraint. Serialized to the
 *  `?action=&actor=&since=&until=&limit=` query string by the client. */
export interface AuditFilters {
  /** Exact action match. */
  action?: string;
  /** Case-insensitive substring match on actor_email. */
  actor?: string;
  /** ISO-8601 lower/upper bounds on `when` (created_at). */
  since?: string;
  until?: string;
  /** Page size; server default 200, capped at 1000. */
  limit?: number;
}

/** Implemented by the HTTP client and the in-memory mock — depend on this, swap backends in tests. */
export interface AccountsApi {
  /** Create the (unverified) account (blobs precomputed with @wiser/e2e). Publishes the pubkey to wiserkeys
   *  server-side. Returns no session — the user must verify by email before they can log in. */
  signup(input: SignupInput): Promise<SignupResult>;
  /** Get a user's salt (+ id) so the client can derive the auth key + KEK for login. Always resolves with a
   *  200-shaped result — an unknown email returns a stable decoy (see LookupResult), not a 404 — so it can't
   *  be used to enumerate which emails have accounts. */
  lookup(email: string): Promise<LookupResult>;
  /** Verify the auth verifier → a session + the wrapped blobs to unwrap locally. Rejects with an
   *  EmailNotVerifiedError (carrying .email) if the account's email is not yet verified. Pass `deviceId` to
   *  participate in the new-device challenge (0015): a login from an unknown device on a gated account returns
   *  a `NewDeviceChallenge` INSTEAD of a session — call `deviceChallenge` to finish. */
  /** `client` (0022) is audit provenance only — the dashboard/app performing the login (e.g. "selfserve",
   *  "console"). Distinct from any service-usage signal; optional and backward-compatible (older callers omit it). */
  login(email: string, authVerifierB64: string, deviceId?: string, issueRefresh?: boolean, client?: string): Promise<LoginResult | NewDeviceChallenge>;
  /** Complete a new-device login: re-prove the password + confirm the emailed PIN for this device → session. */
  deviceChallenge(input: DeviceChallengeInput): Promise<LoginResult>;
  /** Trade a stored refresh token (0017) for a fresh session — silent re-login on app launch. ROTATES the
   *  token (returns a new `refresh_token`; the presented one is consumed). Rejects if revoked/expired, which
   *  the caller should treat as "re-login + re-verify this device". Identity only — no key material. */
  refreshSession(refreshToken: string): Promise<RefreshResult>;
  /** Forget a stored refresh token (opt-out of "keep me signed in" / device sign-out). Idempotent. */
  revokeRefresh(refreshToken: string): Promise<void>;
  /** Confirm an email-verification token (from the emailed link). */
  verifyEmail(token: string): Promise<VerifyEmailResult>;
  /** Re-send the verification email (rate-limited server-side; always resolves). */
  resendVerification(email: string): Promise<void>;
  /** Resolve the current session → the account blobs. */
  me(sessionToken: string): Promise<AccountBlobs>;
  /** Revoke the presented session (logout). Idempotent. */
  logout(sessionToken: string): Promise<void>;
  /** Re-prove the current password, then swap verifier + salt + wrapped master key (kills sessions). */
  changePassword(input: ChangePasswordInput): Promise<void>;
  /** List the signed-in account's active sessions (metadata only; never other tokens). */
  listSessions(sessionToken: string): Promise<SessionInfo[]>;
  /** Revoke a specific session by id, or all-but-current with `all_others` ("sign out everywhere else"). */
  revokeSessions(sessionToken: string, input: RevokeSessionsInput): Promise<void>;
  /** Begin a change-email: email a one-time PIN to `newEmail` (constant-shape; always resolves). */
  changeEmailRequest(sessionToken: string, newEmail: string): Promise<void>;
  /** Complete a change-email with the emailed PIN + current-password proof. Returns the fresh (new-email) blobs. */
  changeEmail(sessionToken: string, input: ChangeEmailInput): Promise<AccountBlobs>;
  /** Self-service soft-delete: re-prove the password + typed confirmation; flags the account and kills sessions. */
  deleteAccount(sessionToken: string, input: DeleteAccountInput): Promise<void>;
  /** Whether the account has a recovery key configured (boolean; never 404). */
  recoveryStatus(email: string): Promise<RecoveryStatusResult>;
  /** Configure (or replace) the account's recovery key (re-proves the password). */
  setupRecovery(input: SetupRecoveryInput): Promise<void>;
  /** Exchange the recovery verifier for the recovery-wrapped master key. */
  recover(email: string, recoveryVerifierB64: string): Promise<RecoverResult>;
  /** Begin a forgot-password reset: email a one-time PIN (constant-shape; always resolves). */
  resetRequest(email: string): Promise<void>;
  /** Forgot-password reset via the recovery key + emailed PIN — swaps verifier + salt + wrapped master key (kills sessions). */
  recoverReset(input: RecoverResetInput): Promise<void>;
  /** Resumable identity feed (Theme A). Read-token gated server-side — pass it as `readToken`. */
  changes(since: number, limit?: number, readToken?: string): Promise<ChangesResult>;
  /** Read one of the signed-in user's per-key UI prefs (datatable view state; 0022). Resolves to the stored
   *  JSON value, or `null` when the key was never set. Scoped server-side to the caller's own user_id — a
   *  session can only ever read its own keys. `key` must match `^[a-zA-Z0-9._:-]{1,128}$`. */
  getPref<T>(sessionToken: string, key: string): Promise<T | null>;
  /** Upsert one of the signed-in user's per-key UI prefs (0022). The value is opaque client-owned layout
   *  state (column widths / visibility / sort / order, filter text) — NOT user content. Rejected server-side
   *  if the serialized value exceeds 32 KB or `key` is malformed. Scoped to the caller's own user_id. */
  setPref(sessionToken: string, key: string, value: unknown): Promise<void>;
}
